import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';
import { Section } from '../components/Section';
import { Heading } from '../components/Heading';
import { Button } from '../components/Button';
import { motion } from 'framer-motion';
import Globe from 'react-globe.gl';
// Removed contact icons as requested

const AboutContainer = styled.div`
  width: 100%;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  position: relative;
  height: 100vh;
  padding-top: 100px; /* Account for standard navbar */
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.1)),
    url('/img_to_use/about.jpg') center/cover;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  z-index: 2;
  padding: 0 2rem;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 4.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.6), 0 0 12px ${theme.colors.accent}33;
  color: ${theme.colors.accent};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2.8rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.6rem;
  margin-bottom: 2.5rem;
  font-weight: 300;
  text-shadow: 1px 1px 6px rgba(0, 0, 0, 0.8);
  color: #FFFFFF; /* Keep subtitle white for better readability */
  opacity: 0.95;
`;

const StatsSection = styled.section`
  background: ${theme.colors.background};
  padding: 4rem 0;
`;

const StatsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  padding: 0 2rem;
`;

const StatCard = styled(motion.div)`
  text-align: center;
  padding: 2rem;
  background: ${theme.colors.sectionAlt};
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.shadow.md};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: ${theme.shadow.xl};
  }
`;

const StatNumber = styled.h3`
  font-size: 3rem;
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.p`
  font-size: 1.1rem;
  color: ${theme.colors.text};
  font-weight: 500;
`;

const MissionSection = styled.section`
  background: ${theme.colors.gradient};
  color: white;
  padding: 6rem 0;
`;

const MissionContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  padding: 0 2rem;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const MissionText = styled.div`
  h2 {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
  }

  p {
    font-size: 1.2rem;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    opacity: 0.9;
  }
`;

const MissionImage = styled.div`
  position: relative;
  
  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
    border-radius: 20px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }
`;

const ValuesSection = styled.section`
  background: white;
  padding: 6rem 0;
`;

const ValuesGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  padding: 0 2rem;
`;

const ValueCard = styled(motion.div)`
  background: linear-gradient(135deg, white, #f8f9fa);
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
  text-align: center;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  }

  h3 {
    font-size: 1.5rem;
    color: ${theme.colors.primary};
    margin-bottom: 1rem;
    font-weight: 600;
  }

  p {
    color: ${theme.colors.text};
    line-height: 1.6;
  }
`;

const GlobalPresenceSection = styled.section`
  background: ${theme.colors.gradient};
  color: ${theme.colors.text};
  padding: 6rem 0;
  position: relative;
  overflow: hidden;
`;

const GlobeContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  padding: 0 2rem;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const GlobeContent = styled.div`
  h2 {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
  }

  p {
    font-size: 1.2rem;
    line-height: 1.8;
    margin-bottom: 2rem;
    opacity: 0.9;
  }
`;

const GlobeWrapper = styled.div`
  height: 400px;
  width: 100%;
  position: relative;
`;

const LocationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
`;

const LocationItem = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  padding: 1rem 1.5rem;
  border-radius: 10px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);

  h4 {
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
    color: ${theme.colors.accent}; /* Your gold accent */
  }

  p {
    margin: 0;
    opacity: 0.8;
    font-size: 0.9rem;
  }
`;

const LeadershipSection = styled.section`
  background: ${theme.colors.background}; /* Your cream background */
  padding: 6rem 0;
`;

const LeadershipContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const LeadershipGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 3rem;
  margin-top: 3rem;
`;

const LeaderCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: ${theme.shadow.md};
  text-align: center;
  transition: all 0.3s ease;
  border: 2px solid ${theme.colors.sectionAlt};

  &:hover {
    transform: translateY(-10px);
    box-shadow: ${theme.shadow.xl};
    border-color: ${theme.colors.accent};
  }
`;

const LeaderImage = styled.div`
  width: 180px;
  height: 180px;
  border-radius: 50%;
  margin: 0 auto 1.5rem;
  overflow: hidden;
  border: 4px solid ${theme.colors.primary};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top; /* Focus on faces, not crop heads */
  }
`;

const LeaderName = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: ${theme.colors.primary};
  font-weight: 600;
`;

const LeaderTitle = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 1rem;
  font-weight: 500;
`;

// Removed bio display; keeping structure minimal

// Removed ContactRow and ContactIcon - no longer needed

const ProcessSection = styled.section`
  background: ${theme.colors.gradient}; /* Your brand orange gradient */
  color: white;
  padding: 6rem 0;
`;

const ProcessContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  text-align: center;
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const ProcessStep = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  padding: 2rem;
  border-radius: 15px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;

  .step-number {
    font-size: 2rem;
    font-weight: 700;
    color: ${theme.colors.accent}; /* Your gold accent */
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }

  p {
    opacity: 0.9;
    line-height: 1.6;
  }
`;

const CodeOfConductSection = styled.section`
  background: ${theme.colors.background};
  padding: 6rem 0;
`;

const CodeOfConductContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const CodeOfConductCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 4rem;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`;

const CodeOfConductTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: 2rem;
  font-family: 'Playfair Display', serif;
`;

const CodeOfConductText = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${theme.colors.text};
  margin-bottom: 2.5rem;
  font-family: 'Inter', sans-serif;
`;

const CodeOfConductButton = styled(Button)`
  margin-top: 1rem;
`;

const AboutPage: React.FC = () => {
  const globeRef = useRef<any>();

  useEffect(() => {
    if (globeRef.current) {
      globeRef.current.pointOfView({ altitude: 2 });
    }
  }, []);

  const regions = [
    { lat: 31.1471, lng: 75.3412, label: 'India', size: 0.6, color: theme.colors.accent },
    { lat: 54.5260, lng: 15.2551, label: 'Europe', size: 0.6, color: theme.colors.accent },
    { lat: 42.3140, lng: -83.2023, label: 'Canton, Michigan', size: 0.6, color: theme.colors.accent }
  ];

  const stats = [
    { number: '2500+', label: 'Successful Placements' },
    { number: '100+', label: 'Partner Companies' },
    { number: '15+', label: 'Years Experience' },
    { number: '95%', label: 'Client Satisfaction' }
  ];

  const values = [
    {
      title: 'Innovation',
      description: 'We embrace cutting-edge technology and innovative approaches to connect the right talent with the right opportunities.'
    },
    {
      title: 'Integrity',
      description: 'We maintain the highest standards of honesty and transparency in all our professional relationships.'
    },
    {
      title: 'Excellence',
      description: 'We are committed to delivering exceptional results that exceed our clients\' and candidates\' expectations.'
    }
  ];

  const leaders = [
    {
      name: 'Abhishek Upadhyay',
      title: 'CEO - De Arc & Managing Partner\nAsia Pacific & Middle East',
      image: '/img_to_use/Abhishek.JPG',
      bio: 'Leads De Arc across Asia Pacific and the Middle East with a focus on strategic growth and client success.',
      email: 'abhishek@dearcglobal.com',
      phone: '+1-123-456-7891'
    },
    {
      name: 'Davinder Grewal',
      title: 'Managing Partner\nAmericas',
      image: '/img_to_use/Davinder Grewal.jpeg',
      bio: 'Drives our presence and delivery across North and South America, building long-term client partnerships.',
      email: 'davinder@dearcglobal.com',
      phone: '+1-123-456-7890'
    },
    {
      name: 'Ajaypartap.S.Grewal',
      title: 'Managing Partner\nEurope',
      image: '/img_to_use/Ajaypartap.jpeg',
      bio: 'Leads European operations with a focus on operational excellence and high-impact talent solutions.',
      email: 'ajaypartap@dearcglobal.com',
      phone: '+1-123-456-7892'
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Consultation',
      description: 'We begin with an in-depth consultation to understand your specific requirements and goals.'
    },
    {
      number: '02',
      title: 'Talent Search',
      description: 'Our expert team conducts comprehensive searches using our global network and advanced tools.'
    },
    {
      number: '03',
      title: 'Screening',
      description: 'Rigorous screening and assessment processes ensure only the best candidates are presented.'
    },
    {
      number: '04',
      title: 'Placement',
      description: 'We facilitate the placement process and provide ongoing support for successful integration.'
    }
  ];

  return (
    <AboutContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            About De ARC Global
          </HeroTitle>
          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Connecting exceptional talent with extraordinary opportunities worldwide
          </HeroSubtitle>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Button variant="primary" size="large">
              Learn More
            </Button>
          </motion.div>
        </HeroContent>
      </HeroSection>

      <StatsSection>
        <StatsGrid>
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <StatNumber>{stat.number}</StatNumber>
              <StatLabel>{stat.label}</StatLabel>
            </StatCard>
          ))}
        </StatsGrid>
      </StatsSection>

      <MissionSection>
        <MissionContent>
          <MissionText>
            <motion.h2
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              Our Mission
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              At De ARC Global, we are dedicated to bridging the gap between exceptional talent and remarkable opportunities across the globe. Our mission is to empower organizations with the right human capital while helping professionals achieve their career aspirations in IT, Healthcare, Engineering, and Logistics sectors.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              We believe that the right match can transform businesses and change lives, which is why we approach every placement with meticulous attention to detail and a deep understanding of both client and candidate needs.
            </motion.p>
          </MissionText>
          <MissionImage>
            <motion.img
              src="/img_to_use/mission (1).jpg"
              alt="Our Mission"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            />
          </MissionImage>
        </MissionContent>
      </MissionSection>

      <ValuesSection>
        <Section>
          <Heading level="h2" align="center">Our Core Values</Heading>
          <ValuesGrid>
            {values.map((value, index) => (
              <ValueCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </ValueCard>
            ))}
          </ValuesGrid>
        </Section>
      </ValuesSection>

      <GlobalPresenceSection>
        <GlobeContainer>
          <GlobeContent>
            <motion.h2
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              Global Presence
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              With offices and partnerships spanning across continents, we provide comprehensive recruitment solutions that transcend geographical boundaries.
            </motion.p>
            <LocationsList>
              {regions.map((region, index) => (
                <LocationItem
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <h4>{region.label}</h4>
                  <p>Strategic operations center</p>
                </LocationItem>
              ))}
            </LocationsList>
          </GlobeContent>
          <GlobeWrapper>
            <Globe
              ref={globeRef}
              globeImageUrl="//unpkg.com/three-globe/example/img/earth-night.jpg"
              pointsData={regions}
              pointAltitude={0.1}
              pointRadius={0.6}
              pointColor={() => theme.colors.accent}
              backgroundColor="rgba(0,0,0,0)"
              width={400}
              height={400}
              enablePointerInteraction={false}
            />
          </GlobeWrapper>
        </GlobeContainer>
      </GlobalPresenceSection>

      <LeadershipSection>
        <LeadershipContent>
          <Heading level="h2" align="center">Our Leadership Team</Heading>
          <LeadershipGrid>
            {leaders.map((leader, index) => (
              <LeaderCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <LeaderImage>
                  <img src={leader.image} alt={leader.name} />
                </LeaderImage>
                <LeaderName>{leader.name}</LeaderName>
                <LeaderTitle style={{ whiteSpace: 'pre-line' }}>{leader.title}</LeaderTitle>
              </LeaderCard>
            ))}
          </LeadershipGrid>
        </LeadershipContent>
      </LeadershipSection>

      <ProcessSection>
        <ProcessContent>
          <Heading level="h2" align="center">Our Process</Heading>
          <ProcessSteps>
            {processSteps.map((step, index) => (
              <ProcessStep
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </ProcessStep>
            ))}
          </ProcessSteps>
        </ProcessContent>
      </ProcessSection>

      <CodeOfConductSection>
        <CodeOfConductContent>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <CodeOfConductCard>
              <CodeOfConductTitle>Our Commitment to Ethical Business Practices</CodeOfConductTitle>
              <CodeOfConductText>
                We are dedicated to upholding the highest standards of ethical business conduct. Our publicly available Code of Conduct serves as a clear guide for our employees and assures our clients that integrity and respect are at the core of everything we do.
              </CodeOfConductText>
              <CodeOfConductButton
                variant="primary"
                size="large"
                onClick={() => window.location.href = '/code-of-conduct'}
              >
                View Code of Conduct
              </CodeOfConductButton>
            </CodeOfConductCard>
          </motion.div>
        </CodeOfConductContent>
      </CodeOfConductSection>
    </AboutContainer>
  );
};

export default AboutPage; 